
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import { useUserSession } from '../contexts/UserContext.js';
import { isDevelopment, getApiUrl } from '../utils/environment.js';
import { isAuthError, getAuthErrorMessage } from '../utils/authErrorUtils.js';
import paypal, { createAccessRequest } from "./paymentApi.js";
import { getCountryFromCode } from '../utils/countryUtils.js';
import { COUNTRY_CODES, filterCountries as filterCountriesUtil, recognizeCountryCode as recognizeCountryCodeUtil } from '../utils/countryCodes.js';
import "./RequestAccessModal.css";
import MobilePlansSwipe from './MobilePlansSwipe.js';
import smileyFace from '../Ui/cool.webp';
import share from '../Ui/share.webp';
import achievedIcon from "../Ui/coins3check.webp";
import notAchievedIcon from "../Ui/coins3-grey.webp";
import legacyBadge from "../Ui/legacy-badge.webp";
import legacyBadgeInactive from "../Ui/legacy-badge-grey.webp";
import contributorBadge from "../Ui/light-bulb-check.webp";
import contributorBadgeInactive from "../Ui/light-bulb-grey.webp";
import approval from "../Ui/lockcheck.webp";
import approvalInactive from "../Ui/lock-grey.webp";
import line from "../Ui/wave.webp";
// import dots from "../Ui/line-chart.png";
import flag from "../Ui/finish3.webp";
import flagInactive from "../Ui/finish2.webp";

const RequestAccessModal = ({ onClose }) => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, user, logout } = useAuth0();
  const { userSession, getModalStep, updateUserSession, loadUserSession } = useUserSession();
  
  // Check if we're on localhost for dev mode and verification logic - SECURE IMPLEMENTATION
  const isLocalhost = useMemo(() => 
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    []
  );
  const allowDevBypass = useMemo(() => isLocalhost && isDevelopment(), [isLocalhost]);
  
  // Helper to handle errors - checks for auth errors and triggers logout if needed
  const handleError = useCallback((error, fallbackMessage = 'An error occurred. Please try again.') => {
    if (isAuthError(error)) {
      // Session expired - show message and logout
      setError(getAuthErrorMessage(error));
      setTimeout(() => {
        logout({ logoutParams: { returnTo: window.location.origin } });
      }, 2000); // Give user time to see the message
      return true; // Indicates auth error was handled
    }
    // Regular error - show the error message
    if (error.name === 'TypeError' && error.message?.includes('Failed to fetch')) {
      setError('Unable to connect to the server. Please check your internet connection and try again.');
    } else {
      setError(error.message || fallbackMessage);
    }
    return false;
  }, [logout]);
  
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(110);
  const [isLoading, setIsLoading] = useState(false);
  const [uniqueLink, setUniqueLink] = useState("");
  const [referrals, setReferrals] = useState(0);
  const [alreadyPaidClicks, setAlreadyPaidClicks] = useState(0);

  // Verification states
  const [verificationStep, setVerificationStep] = useState('phone'); // 'phone', 'email-request', 'email-verify'
  const [verificationPhone, setVerificationPhone] = useState('');
  const [verificationCountryCode, setVerificationCountryCode] = useState('+44'); // Default to UK
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  
  // Country code input states
  const [countryCodeInput, setCountryCodeInput] = useState('+44');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState([]);

  // Filter countries based on input - uses pre-computed data from countryCodes.js
  const filterCountries = useCallback((input) => {
    return filterCountriesUtil(input, 5);
  }, []);

  // Handle country selection from dropdown
  const selectCountry = useCallback((countryData) => {
    setCountryCodeInput(countryData.code);
    setVerificationCountryCode(countryData.code);
    setShowCountryDropdown(false);
    setFilteredCountries([]);
  }, []);

  // Recognize country code from input - uses utility from countryCodes.js
  const recognizeCountryCode = useCallback((input) => {
    return recognizeCountryCodeUtil(input);
  }, []);

  const handleCountryCodeInput = useCallback((value) => {
    setCountryCodeInput(value);
    
    if (value.trim()) {
      const filtered = filterCountries(value);
      setFilteredCountries(filtered);
      setShowCountryDropdown(filtered.length > 0);
    } else {
      setFilteredCountries([]);
      setShowCountryDropdown(false);
    }
  }, [filterCountries]);

  // Simple function to refresh user data when needed
  const refreshUserData = useCallback(async (forceRefresh = false) => {
    await loadUserSession(forceRefresh);
  }, [loadUserSession]);

  // Initialize modal based on user session - this is the ONLY source of truth
  useEffect(() => {
    if (userSession.isLoaded) {
      // Unauthenticated users should always start at step 1
      if (!isAuthenticated) {
        setNeedsVerification(false);
        setStep(1);
        return;
      }
      
      // In dev mode, allow dev tools to simulate verification states
      if (allowDevBypass) {
        // Check if dev tools are simulating verification states
        const needsEmailVerification = !userSession.user?.email || !userSession.user?.isEmailVerified;
        const needsPhoneVerification = !userSession.user?.phone;
        
        if (needsEmailVerification || needsPhoneVerification) {
          setNeedsVerification(true);
          setStep(0); // Verification step
          
          // Set verification email from user data if available
          if (userSession.user?.email && userSession.user.email.includes('@')) {
            setVerificationEmail(userSession.user.email);
          }
          if (userSession.user?.phone) {
            setVerificationPhone(userSession.user.phone);
          }
          
          // Determine which verification step to start with
          // In localhost, allow dev tools to override verification step via user.devVerificationStep
          if (userSession.user?.devVerificationStep) {
            setVerificationStep(userSession.user.devVerificationStep);
          } else if (!userSession.user?.phone) {
            setVerificationStep('phone');
          } else if (!userSession.user?.isEmailVerified) {
            setVerificationStep('email-request');
          }
        } else {
          // Localhost dev mode - no verification needed, using normal modal steps
          setNeedsVerification(false);
          const correctStep = getModalStep();
          setStep(correctStep);
          setMessage(userSession.reasonToJoin || "");
          setIsTermsChecked(userSession.termsAccepted || false);
        }
      } else {
        // Check verification status using the new stepped logic
        checkVerificationStatusFromBackend().catch(() => {});
      }
      
      if (userSession.user?.referralCount >= 0) {
        setReferrals(userSession.user.referralCount);
      }
      
      // Set referral link
      if (userSession.user?.referralCode) {
        const referralLink = `https://intellegacy.app/register?ref=${userSession.user.referralCode}`;
        setUniqueLink(referralLink);
      }
    }
  }, [userSession.isLoaded, userSession.hasPaid, userSession.reasonToJoin, userSession.termsAccepted, userSession.planType, userSession.user?.referralCount, userSession.user?.referralCode, allowDevBypass, isAuthenticated]);

  // Real-time updates for dev tools - watch for user session changes
  useEffect(() => {
    if (!userSession.isLoaded) return;
    
    // Only react to dev tool changes if user is authenticated
    if (!isAuthenticated) return;
    
    // In dev mode, react to dev tool changes in real-time
    if (allowDevBypass) {
      
      // Check if dev tools are simulating verification states
      const needsEmailVerification = !userSession.user?.email || !userSession.user?.isEmailVerified;
      const needsPhoneVerification = !userSession.user?.phone;
      
      if (needsEmailVerification || needsPhoneVerification) {
        setNeedsVerification(true);
        setStep(0); // Verification step
        
        // Set verification email from user data if available
        if (userSession.user?.email && userSession.user.email.includes('@')) {
          setVerificationEmail(userSession.user.email);
        }
        if (userSession.user?.phone && userSession.user.phone !== 'undefined' && userSession.user.phone !== undefined) {
          setVerificationPhone(userSession.user.phone);
        }
        
        // Determine which verification step to show
        if (userSession.user?.devVerificationStep) {
          setVerificationStep(userSession.user.devVerificationStep);
        } else if (!userSession.user?.phone) {
          setVerificationStep('phone');
        } else if (!userSession.user?.isEmailVerified) {
          setVerificationStep('email-request');
        }
      } else {
        setNeedsVerification(false);
        const correctStep = getModalStep();
        setStep(correctStep);
        setMessage(userSession.reasonToJoin || "");
        setIsTermsChecked(userSession.termsAccepted || false);
      }
    }
  }, [userSession.user?.email, userSession.user?.isEmailVerified, userSession.user?.phone, userSession.user?.devVerificationStep, userSession.reasonToJoin, userSession.termsAccepted, userSession.hasPaid, userSession.planType, allowDevBypass, getModalStep, isAuthenticated]);

  // reactive mobile flag for safe conditional rendering
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => {
      if (typeof window === 'undefined') return setIsMobile(false);
      setIsMobile(window.innerWidth <= 768);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const POST_AUTH_STEP_KEY = 'postAuthStep';

  // Check verification status using backend's new stepped logic
  const checkVerificationStatusFromBackend = async () => {
    try {
      const cleanApiUrl = getApiUrl().replace(/\/$/, '');
      const token = await getAccessTokenSilently();
      
      const response = await fetch(`${cleanApiUrl}/api/verification/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        // Fallback to original logic if backend call fails
        const needsEmailVerification = !userSession.user?.email || !userSession.user?.isEmailVerified;
        const needsPhoneVerification = !userSession.user?.phone;
        
        if (needsEmailVerification || needsPhoneVerification) {
          setNeedsVerification(true);
          setStep(0);
          setVerificationStep(!userSession.user?.phone ? 'phone' : 'email-request');
        } else {
          setNeedsVerification(false);
          const correctStep = getModalStep();
          setStep(correctStep);
          setMessage(userSession.reasonToJoin || "");
          setIsTermsChecked(userSession.termsAccepted || false);
        }
        return;
      }
      
      const result = await response.json();
      
      // Validate response structure
      if (!result || typeof result.needsVerification !== 'boolean') {
        throw new Error('Invalid response from verification service');
      }
      
      if (result.needsVerification) {
        setNeedsVerification(true);
        setStep(0); // Verification step
        
        // Set the verification step based on backend's determination
        if (result.nextStep === 'phone') {
          setVerificationStep('phone');
        } else if (result.nextStep === 'email-input') {
          setVerificationStep('email-request');
          // Don't pre-fill email since user doesn't have one
        } else if (result.nextStep === 'email-verify') {
          setVerificationStep('email-request');
          // Pre-fill email from backend if user has one
          if (result.status?.email && result.status.email.includes('@')) {
            setVerificationEmail(result.status.email);
          }
        }
        
        // Set phone if user has one
        if (userSession.user?.phone) {
          setVerificationPhone(userSession.user.phone);
        }
      } else {
        // No verification needed - proceed to normal onboarding flow
        setNeedsVerification(false);
        const correctStep = getModalStep();
        setStep(correctStep);
        setMessage(userSession.reasonToJoin || "");
        setIsTermsChecked(userSession.termsAccepted || false);
      }
    } catch (error) {
      // Fallback to original logic on error
      const needsEmailVerification = !userSession.user?.email || !userSession.user?.isEmailVerified;
      const needsPhoneVerification = !userSession.user?.phone;
      
      if (needsEmailVerification || needsPhoneVerification) {
        setNeedsVerification(true);
        setStep(0);
        setVerificationStep(!userSession.user?.phone ? 'phone' : 'email-request');
      } else {
        setNeedsVerification(false);
        const correctStep = getModalStep();
        setStep(correctStep);
        setMessage(userSession.reasonToJoin || "");
        setIsTermsChecked(userSession.termsAccepted || false);
      }
    }
  };

  // Verification handlers
  const handlePhoneSubmit = async () => {
    if (verificationLoading) return; // Prevent double submission
    
    if (!verificationPhone.trim()) {
      setError("Please enter your phone number.");
      return;
    }
    
    // Basic phone validation
    const phoneRegex = /^[\d\s\-\(\)]{7,15}$/;
    if (!phoneRegex.test(verificationPhone.trim())) {
      setError("Please enter a valid phone number (7-15 digits).");
      return;
    }
    
    setVerificationLoading(true);
    setError("");
    
    try {
      const cleanApiUrl = getApiUrl().replace(/\/$/, '');
      const token = await getAccessTokenSilently();
      
      // Combine country code with phone number
      const fullPhoneNumber = `${verificationCountryCode}${verificationPhone.replace(/[\s\-\(\)]/g, '')}`;
      
      // Get country name from country code using shared utility
      const countryName = getCountryFromCode(verificationCountryCode);
      
      const response = await fetch(`${cleanApiUrl}/api/verification/phone/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          phoneNumber: fullPhoneNumber,
          country: countryName
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
      
      // Check what the next verification step should be after phone update
      await refreshUserData(); // Refresh to get updated user data
      
      try {
        await checkVerificationStatusFromBackend(); // Determine next step
      } catch (verificationError) {
        // Fallback: assume email verification is next
        setVerificationStep('email-request');
      }
      
    } catch (error) {
      handleError(error, "Failed to update phone number. Please try again.");
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleEmailVerificationRequest = async () => {
    if (verificationLoading) return; // Prevent double submission
    
    if (!verificationEmail.trim()) {
      setError("Please enter your email address");
      return;
    }
    
    // Validate email format (matching backend validation)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(verificationEmail.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    
    setVerificationLoading(true);
    setError("");
    
    try {
      const cleanApiUrl = getApiUrl().replace(/\/$/, '');
      const token = await getAccessTokenSilently();
      
      const response = await fetch(`${cleanApiUrl}/api/verification/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: verificationEmail })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
      
      // Move to code verification step
      setVerificationStep('email-verify');
      
    } catch (error) {
      handleError(error, "Failed to send verification email. Please try again.");
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleEmailCodeVerify = async () => {
    if (verificationLoading) return; // Prevent double submission
    
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }
    
    setVerificationLoading(true);
    setError("");
    
    try {
      const cleanApiUrl = getApiUrl().replace(/\/$/, '');
      const token = await getAccessTokenSilently();
      
      const response = await fetch(`${cleanApiUrl}/api/verification/email/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: verificationEmail, code: verificationCode })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
      
      // Email verification complete - refresh user data and proceed to normal flow
      await refreshUserData();
      setNeedsVerification(false);
      const correctStep = getModalStep();
      setStep(correctStep);
      
    } catch (error) {
      // Check if it's an auth error first (session expired)
      if (!handleError(error)) {
        // Not an auth error - check for specific verification errors
        if (error.message?.includes('Invalid') || error.message?.includes('expired')) {
          setError("The verification code is invalid or has expired. Please request a new code.");
        }
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  // Step progression handler with backend updates  
  const handleNextStep = async () => {
    setError("");
    setIsLoading(true);

    try {
      // Step 1: Save reason and proceed (user is already authenticated)
      if (step === 1) {
        if (!message.trim()) {
          setError("We would rather give the opportunity to those who really want it.");
          setIsLoading(false);
          return;
        }
        
        if (message.trim().length < 10) {
          setError("Please write at least 10 characters explaining why you want to join.");
          setIsLoading(false);
          return;
        }
        
        // User is already authenticated from button click - save their reason
        try {
          const token = await getAccessTokenSilently();
          const cleanApiUrl = getApiUrl().replace(/\/$/, '');
          
          await fetch(`${cleanApiUrl}/api/user/profile`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
              reasonToJoin: message.trim()
            })
          });
          
          // Update local context
          updateUserSession({ 
            reasonToJoin: message.trim()
          });
          
          // Move to plan selection
          setStep(2);
          return;
        } catch (error) {
          handleError(error, "Failed to save your reason. Please try again.");
          return;
        }
      }

      // Step 2: Save plan selection and proceed (user is already authenticated)
      if (step === 2) {
        if (!isTermsChecked) {
          setError("Please agree to the terms and conditions to proceed.");
          setIsLoading(false);
          return;
        }
        
        // User is already authenticated from button click - save their progress
        try {
          const token = await getAccessTokenSilently();
          const cleanApiUrl = getApiUrl().replace(/\/$/, '');
          
          const response = await fetch(`${cleanApiUrl}/api/user/profile`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
              planType: 'Elite',
              termsAccepted: true
            })
          });
          
          if (!response.ok) {
            throw new Error(`PATCH failed: ${response.status} ${response.statusText}`);
          }
          
          await response.json();
          
          // Update local context
          updateUserSession({ 
            planType: 'Elite', 
            termsAccepted: true 
          });
          
          // Move directly to payment step
          setStep(3);
          return;
        } catch (error) {
          handleError(error, "Failed to save your selection. Please try again.");
          return;
        }
      }

      // Step 3: Payment validation
      if (step === 3 && !userSession.hasPaid) {
        setError("Please complete the payment to continue.");
        setIsLoading(false);
        return;
      }

      // Progress to next step
      setStep(step + 1);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "already paid" button clicks with escalating responses
  const handleAlreadyPaidClick = () => {
    if (alreadyPaidClicks === 0) {
      setAlreadyPaidClicks(1);
      alert("No you haven't");
    } else if (alreadyPaidClicks === 1) {
      setAlreadyPaidClicks(2);
      alert("You may have authenticated in a different way. Try logging out and back in with the credentials you used the first time. If you're certain you've paid but can not find your account - our support team will help you restore access. Please email info@intellegacy.app and Thank you for your patience!");
    }
    // After 2 clicks, button should disappear (handled in render)
  };

  // Price animation effect
  useEffect(() => {
    if (step === 2) {
      let price = 110;
      const totalDuration = 3900; 
      const steps = price - 3; 
      const intervalTime = totalDuration / steps;
  
      const interval = setInterval(() => {
        setTimeout(() => {
          
        }, 2300);
        if (price > 3) {
          price -= 1;
          setCurrentPrice(price);
        } else {
          clearInterval(interval);
        }
      }, intervalTime);
  
      return () => clearInterval(interval);
    }
  }, [step]);

  // Smart overflow management based on modal height
  useEffect(() => {
    const handleModalOverflow = () => {
      const modalContent = document.querySelector('.modal-content');
      if (!modalContent) return;
      
      const modalHeight = modalContent.offsetHeight;
      const viewportHeight = window.innerHeight;
      const maxAllowedHeight = viewportHeight * 0.90; // 90vh

      if (modalHeight > maxAllowedHeight) {
        modalContent.style.overflowY = 'auto';
        modalContent.style.maxHeight = '83vh';
      } else {
        modalContent.style.overflowY = 'visible';
        modalContent.style.maxHeight = 'none';
      }
    };

    // Check on mount and whenever step changes
    const timeoutId = setTimeout(handleModalOverflow, 100);
    
    // Also check on window resize
    window.addEventListener('resize', handleModalOverflow);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleModalOverflow);
    };
  }, [step, userSession.hasPaid]); // Re-run when step changes or payment status changes

useEffect(() => {
  // Only initialize PayPal if we're on step 3 AND user hasn't already paid
  if (step !== 3 || userSession.hasPaid) {
    return;
  }
  
  
  // Initialize PayPal without duplicate payment checks
  const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  if (!PAYPAL_CLIENT_ID) {
    setError('PayPal configuration error. Please try again later.');
    return;
  }

  async function renderPayPalButtons() {
    const container = document.getElementById('paypal-buttons');
    if (!container) return;
    
    if (container.children.length > 0) {
      return;
    }
    try {
      await window.paypal.Buttons({
        style: {
          layout: "vertical",
          color: "black", 
          shape: "rect",
          label: "paypal"
        },
        onClick: () => {
          // Clear any previous errors when user clicks PayPal button
          setError('');
        },
        createOrder: async (data, actions) => {
          try {
            // Show loading state
            setIsLoading(true);
            setError('');
            
            const auth0Id = user?.sub;
            const token = await getAccessTokenSilently();
            if (!auth0Id) throw new Error('User not authenticated');
            if (!userSession.planType || userSession.planType === 'Unassigned') {
              throw new Error('Please select a plan before proceeding to payment');
            }
            
            const cleanApiUrl = getApiUrl().replace(/\/$/, '');
            
            const response = await fetch(`${cleanApiUrl}/api/paypal/create-order`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                plan: userSession.planType,
                price: 3.00,
                auth0Id
              })
            });

            // Hide loading - PayPal popup will handle the rest
            setIsLoading(false);

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              if (errorData.code === 'ALREADY_PAID') {
                // Update UserContext and let it handle the state change
                updateUserSession({ hasPaid: true });
                setError('');
                throw new Error('Payment already completed');
              }
              throw new Error(errorData.error || `Failed to create order (${response.status})`);
            }

            const orderData = await response.json();
            if (!orderData.success) throw new Error(orderData.error || 'Failed to create order');
            return orderData.orderId;
          } catch (error) {
            setIsLoading(false);
            // Check if it's a session error before showing generic payment error
            if (isAuthError(error)) {
              handleError(error);
            } else {
              setError(`Order creation failed: ${error.message}`);
            }
            throw error;
          }
        },
        onApprove: async (data, actions) => {
          try {
            // Show loading during payment capture
            setIsLoading(true);
            setError('');
            
            const auth0Id = user?.sub;
            const token = await getAccessTokenSilently();
            if (!auth0Id) throw new Error('User not authenticated');
            if (!userSession.planType || userSession.planType === 'Unassigned') {
              throw new Error('Please select a plan before proceeding to payment');
            }
            
            const cleanApiUrl = getApiUrl().replace(/\/$/, '');
            
            const response = await fetch(`${cleanApiUrl}/api/paypal/capture-order`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                orderId: data.orderID,
                plan: userSession.planType,
                price: 3.00
              })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.error || `Payment capture failed with status ${response.status}`);
            }
            
            const captureData = await response.json();
            if (!captureData.success) {
              throw new Error(captureData.error || 'Payment capture failed');
            }

            // Create access request with the user's reason
            await createAccessRequest({ auth0Id, token, user, reason: message });
            
            // Update UserContext with payment status
            updateUserSession({ hasPaid: true });
            
            // Refresh user data to get latest referral info (force refresh to bypass cache)
            await refreshUserData(true);
            
            setIsLoading(false);
            setError('');
            
            // Immediately jump to final step (invite friends page)
              setStep(4);
          } catch (err) {
            setIsLoading(false);
            setError(`Payment completion failed: ${err.message}`);
            throw err;
          }
        },
        onCancel: () => {
          setIsLoading(false);
          setError('Payment was cancelled. Please try again.');
        },
        onError: (err) => {
          setIsLoading(false);
          setError('Payment failed. Please try again or contact support at info@intellegacy.app');
        },
        onClose: () => {
          // User closed the PayPal popup without completing
          setIsLoading(false);
        }
      }).render('#paypal-buttons');
    } catch (err) {
      setError('Failed to initialize payment. Please try again.');
    }
  }

  // Setup PayPal SDK with better error handling and retry mechanism
  const loadPayPalSDK = () => {
    return new Promise((resolve, reject) => {
      // Check if PayPal SDK is already loaded and functional
      if (window.paypal && window.paypal.Buttons) {
        // PayPal SDK already available
        resolve();
        return;
      }

      // Remove existing script if it exists but isn't working
      const existingScript = document.getElementById('paypal-sdk');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.id = 'paypal-sdk';
      // Use minimal configuration to avoid loading issues
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=GBP&intent=capture&components=buttons`;
      script.async = true;
      
      let loadTimeout = setTimeout(() => {
        script.remove();
        reject(new Error('PayPal SDK load timeout'));
      }, 15000); // 15 second timeout

      script.onload = () => {
        clearTimeout(loadTimeout);

        if (window.paypal && window.paypal.Buttons) {
          resolve();
        } else {
          reject(new Error('PayPal SDK loaded but not functional'));
        }
      };
      
      script.onerror = (err) => {
        clearTimeout(loadTimeout);
        script.remove();
        reject(new Error('Failed to load PayPal SDK'));
      };
      
      document.body.appendChild(script);
    });
  };

  // Load SDK and render buttons with session management
  const initializePayPal = async () => {
    try {
      // Only initialize if not already done
      if (window.paypal && document.getElementById('paypal-buttons')?.children.length > 0) {
        return;
      }
      
      await loadPayPalSDK();
      renderPayPalButtons();
    } catch (sdkError) {
      setError('Failed to initialize payment system. Please check your internet connection and try again.');
    }
  };
  
  initializePayPal();
}, [step, user, getAccessTokenSilently, userSession.hasPaid]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && !event.target.closest('.country-code-autocomplete')) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown]);

  const progressMilestones = [
    { label: (<p><b>Joined</b> <br/>(3 Legacy coins)</p>), achieved: referrals >= 0, icon: achievedIcon, inactiveIcon: notAchievedIcon },
    { label: " ", achieved: referrals >= 0, icon: line },
    { label: (<p><b>1 Friend</b> <br/>(3 Legacy coins)</p>), achieved: referrals >= 1, icon: achievedIcon, inactiveIcon: notAchievedIcon },
    { label: " ", achieved: referrals >= 1, icon: line },
    { label: (<p>2 Friends <br/>(Contributor badge)</p>), achieved: referrals >= 2, icon: contributorBadge, inactiveIcon: contributorBadgeInactive },
    { label: " ", achieved: referrals >= 2, icon: line },
    { label: (<p>3 Friends <br/>(5 Legacy coins)</p>), achieved: referrals >= 3, icon: achievedIcon, inactiveIcon: notAchievedIcon },
    { label: " ", achieved: referrals >= 3, icon: line },
    { label: (<p>4 Friends <br/>(80% chance of approval)</p>), achieved: referrals >= 4, icon: approval, inactiveIcon: approvalInactive },
    { label: " ", achieved: referrals >= 4, icon: line },
    { label: (<p>5 Friends <br/>(16 Legacy coins)</p>), achieved: referrals >= 5, icon: achievedIcon, inactiveIcon: notAchievedIcon },
    { label: " ", achieved: referrals >= 5, icon: flag, inactiveIcon: flagInactive },
    { label: (<p>Validation <br/>(Legacy badge)</p>), achieved: false, icon: legacyBadge, inactiveIcon: legacyBadgeInactive },
  ];

  const plans = [
    {
      name: "Basic Plan",
      price: "£32 per year",
      features: [
        "Language model with memory & conversational features",
        "100GB storage to build your life library",
        "Basic voice cloning",
        "Basic visual cloning capabilities",
        "Effective sync across platforms",
        "Limited finetuning for some models",
      ],
    },
    {
      name: "Pro Plan",
      price: "£71 per year",
      features: [
        "Language model with personalized memory & conversational features",
        "500GB storage to build your life library",
        "Advanced voice cloning",
        "Improved visual cloning capabilities",
        "Effective sync across platforms",
        "Finetuning for all models",
      ],
    },
    {
      name: (
        <div className="wordCarousel">
        <div>
          <ul className="flip">
            <li>Elite Plan</li>
            <li>
            <span className="btn-shine">Early Access</span>
            </li>
          </ul>
        </div>
      </div>
      ),
      price: `£${currentPrice} per first year`,
      features: [
        "Enhanced Language model with personalized memory, training on your data & conversational features",
        "1TB storage to build your life library",
        "Advanced voice cloning",
        "Cutting edge visual cloning capabilities",
        "Effective sync across platforms",
        "Finetuning for all models",
        "First access to new features",
      ],
    },
  ];
 
  return (
    <>
      {(isLoading || verificationLoading) && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      <div className="request-access-modal">
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            ✖
          </button>
        {showTerms ? (
          <div className="terms-modal">
            <h2 className="modal-h2">Terms and Conditions</h2>


<div className="terms-container">
  <h1 className="terms-heading">Terms and Conditions for Early Access to <strong>Intellegacy</strong></h1>
  <p className="terms-paragraph">Effective Immediately</p>

  <h2 className="terms-subheading">1. Introduction</h2>
  <p className="terms-paragraph">
    These Terms and Conditions ("Terms") constitute a legally binding agreement between you (the "User") and Intellegacy, governing your voluntary participation in the Early Access program for Intellegacy (the "App"), a digital software product currently in an experimental, pre-release stage of development. By proceeding to submit a request and corresponding payment for early access, you expressly acknowledge that you have read, understood, and agreed to be bound by the entirety of these Terms. If you do not accept or agree with any part of these Terms, you are not authorized to proceed with registration, access, or payment. These Terms are subject to change at any time in accordance with Clause 13, and your continued participation constitutes acceptance of such modifications.
  </p>

  <h2 className="terms-subheading">2. Eligibility</h2>
  <ul className="terms-list">
    <li className="terms-list-item">
      You must be a natural person aged 18 years or older at the time of submission, or provide verifiable legal consent from a parent or legal guardian. Organizations, bots, or automated systems are not permitted to apply or participate without prior written authorization from Intellegacy.
    </li>
    <li className="terms-list-item">
      You must have the full legal capacity and authority to enter into a binding agreement in accordance with the laws of your jurisdiction. By proceeding, you confirm that no legal, regulatory, or contractual restrictions prohibit your participation.
    </li>
    <li className="terms-list-item">
      Intellegacy reserves the right, at its sole discretion, to deny access to any user for any reason, including but not limited to suspected misrepresentation, fraud, or prior breach of terms in unrelated engagements. Participation is a privilege, not a right.
    </li>
  </ul>

  <h2 className="terms-subheading">3. Privacy and Information Use</h2>
  <p className="terms-paragraph">
    By enrolling in the Early Access program, you grant Intellegacy permission to collect, store, process, and analyze your data—including submitted content, metadata, behavioral interaction patterns, and usage frequency—for the purpose of developing, training, evaluating, and refining personalized and humanized artificial intelligence models. These models may reflect and incorporate generalized learnings derived from aggregate user behaviors but will not disclose your individual data unless explicitly authorized by you in writing. Your data may be used in anonymized and statistical forms to improve system performance, optimize product-market fit, guide ethical alignment strategies, and conduct non-personally identifiable research in user interaction trends, digital cognition, and memory simulation.
  </p>
  <p className="terms-paragraph">
    Intellegacy shall make commercially reasonable efforts to maintain the security, integrity, and confidentiality of your personal information, including application of standard encryption protocols and controlled access procedures. However, no data transmission or storage method is guaranteed secure, and by participating, you acknowledge and accept this residual risk.
  </p>
  <p className="terms-paragraph">
    You retain absolute control over the visibility and disclosure of your personal content. Unless and until you provide express consent, no portion of your profile, uploads, preferences, or AI model behavior will be made publicly viewable or externally accessible. All user accounts during the Early Access phase are private by default, sealed against unsolicited access, and accessible only to the registered user. Although interaction between accounts is technically supported—for example, through contribution-based exchanges akin to collaborative development or knowledge curation—such features do not imply or permit the public release of any content. All shared interaction remains confined within the controlled private environment of the platform.
  </p>

  <h2 className="terms-subheading">4. Early Access Fee</h2>
  <p className="terms-paragraph">
    The early access request fee is £3, payable once via accepted digital payment platforms. This fee is strictly non-refundable under all circumstances, unless mandated by statutory consumer protection law in your jurisdiction. The fee serves solely as consideration for Intellegacy's administrative time and technical overhead associated with reviewing your request. It does not constitute a guarantee, promise, or entitlement to access, admission, onboarding, or inclusion in the Early Access environment.
  </p>
  <p className="terms-paragraph">
    Selection of participants is discretionary, limited in capacity, and subject to prioritization logic based on application volume, project needs, referral activity, technical capability, regional balance, or other non-public eligibility criteria. Submission of a fee does not create a service-level agreement, contract of service, or expectation of delivery. By submitting payment, you waive any right to refund, reimbursement, restitution, or compensation in the event that your request is declined, delayed, deprioritized, or receives no response.
  </p>
  <p className="terms-paragraph">
    Intellegacy further reserves the right to revoke or retract acceptance at any stage before formal onboarding, including after provisional approval, without any financial liability, provided no material services have yet been rendered. The payment is a precondition for queue placement only and does not imply ongoing rights, usage duration, or access level guarantees.
  </p>

  <h2 className="terms-subheading">5. Nature of Early Access</h2>
  <p className="terms-paragraph">
    You acknowledge and accept that the App is in an alpha or early beta phase, undergoing active iteration. It may exhibit bugs, inconsistencies, feature instability, data loss, partial outages, unresponsiveness, or other conditions not suitable for commercial deployment or critical task use. Intellegacy makes no representations that the Early Access version will resemble the eventual public product, if one is released.
  </p>
  <p className="terms-paragraph">
    Your use of the App is at your own risk. Any reliance on content generated, actions performed, or conclusions suggested by the AI is entirely your responsibility. Intellegacy disclaims all liability for decisions made based on outputs from the App. The program is offered for feedback gathering, model training, and testing purposes only.
  </p>
  <p className="terms-paragraph">
    Intellegacy reserves the right to suspend or terminate the Early Access program at any time without notice, refund, or further development. No assurance is given that a public release will occur, nor that current participants will be invited to transition to future versions or tiers.
  </p>

  <h2 className="terms-subheading">6. Limited License</h2>
  <p className="terms-paragraph">
    If granted access, you are authorized to use the App under a revocable, non-transferable, non-exclusive, limited license strictly for personal, non-commercial, exploratory use. This license is granted solely for the duration of the Early Access period and may be modified, suspended, or terminated at any time for any reason. You may not reproduce, modify, distribute, or create derivative works based on the App. All rights not explicitly granted are reserved by Intellegacy.
  </p>

  <h2 className="terms-subheading">7. Restrictions</h2>
  <ul className="terms-list">
    <li className="terms-list-item">You shall not share, sell, license, lend, rent, or otherwise disclose access credentials or account information.</li>
    <li className="terms-list-item">You shall not copy, modify, translate, disassemble, reverse engineer, or decompile any part of the App or attempt to extract the underlying code, architecture, or logic.</li>
    <li className="terms-list-item">You shall not interfere with or circumvent any access controls, encryption, authentication mechanisms, or usage monitoring systems implemented in the App.</li>
    <li className="terms-list-item">You shall not submit or generate content that is illegal, harmful, abusive, harassing, defamatory, or in violation of any applicable laws or third-party rights.</li>
    <li className="terms-list-item">You shall not engage in any unauthorized penetration testing, security probing, or vulnerability exploitation, including injection attacks, token tampering, or artificial traffic generation.</li>
  </ul>

  <h2 className="terms-subheading">8. Intellectual Property</h2>
  <p className="terms-paragraph">
    All software code, system architecture, interface design, brand identity, training data, and content created or displayed within the App are the exclusive intellectual property of Intellegacy and/or its licensors. No title, ownership interest, or intellectual property rights are transferred to you through your participation. You agree not to copy, alter, misappropriate, or reuse any proprietary materials without explicit written consent. Any feedback, suggestion, or bug report submitted by you may be used by Intellegacy in any manner without compensation or obligation.
  </p>

  <h2 className="terms-subheading">9. No Warranties</h2>
  <p className="terms-paragraph">
    The App is provided “as is” and “as available” with no warranties, express or implied. Intellegacy disclaims all warranties including merchantability, fitness for a particular purpose, accuracy, performance, and non-infringement. Intellegacy does not warrant that the App will meet your requirements, operate without error, or produce reliable or meaningful outputs. Any expectations of performance or utility are at your own risk.
  </p>

  <h2 className="terms-subheading">10. Limitation of Liability</h2>
  <p className="terms-paragraph">
    To the maximum extent permitted by law, Intellegacy shall not be liable for any damages arising out of or related to your use of the App, including but not limited to loss of profits, data breaches, security compromise, unauthorized access, business disruption, or reputational damage. In no event shall Intellegacy’s cumulative liability exceed the amount paid by you for the Early Access fee.
  </p>

  <h2 className="terms-subheading">11. Indemnification</h2>
  <p className="terms-paragraph">
    You agree to defend, indemnify, and hold harmless Intellegacy, its officers, directors, developers, contractors, and affiliates from any claims, liabilities, losses, costs, or damages arising from your violation of these Terms or misuse of the App, including any breach of data laws, intellectual property rights, or applicable regulations.
  </p>

  <h2 className="terms-subheading">12. Termination</h2>
  <p className="terms-paragraph">
    Intellegacy reserves the right to suspend, restrict, or permanently terminate your access at any time, with or without cause or notice. Breach of any provision in these Terms may result in immediate revocation of access, forfeiture of participation, and potential legal enforcement. Termination does not relieve you of your obligations under these Terms.
  </p>

  <h2 className="terms-subheading">13. Changes to Terms</h2>
  <p className="terms-paragraph">
    These Terms may be revised at any time. Intellegacy will make reasonable efforts to notify users of changes via email or in-app notification. Continued participation constitutes acceptance of the updated Terms. Users are encouraged to review this page regularly for changes.
  </p>

  <h2 className="terms-subheading">14. Governing Law</h2>
  <p className="terms-paragraph">
    These Terms are governed by the laws of the United Kingdom. You agree that any dispute or claim arising from or relating to these Terms or the App shall be resolved exclusively in the courts of England and Wales, and you submit to their jurisdiction and venue.
  </p>

  <h2 className="terms-subheading">15. Contact</h2>
  <p className="terms-paragraph">
    If you have any questions about these Terms, please contact us at <a href="mailto:info@intellegacy.app">info@intellegacy.app</a>.
  </p>

  <h2 className="terms-subheading">16. Entire Agreement</h2>
  <p className="terms-paragraph">
    These Terms, together with any policies, disclaimers, or documents referenced herein, constitute the entire agreement between you and Intellegacy with respect to Early Access and supersede all prior agreements, oral or written, relating to the same subject matter. No waiver or modification of these Terms shall be effective unless in writing and executed by an authorized representative of Intellegacy.
  </p>



<br/>
            <button className="blueButt" onClick={() => setShowTerms(false)}>
              Back
            </button><br/><br/><br/><br/>

</div>


          </div>
        ) : step === 0 && needsVerification ? (
          <div className="verification-step">
            {verificationStep === 'phone' ? (
              <div className="phone-verification">
                <h2 className="modal-h2">Update Your Information</h2>
                <p className="modalText">
                  We need to verify your contact information to complete your registration.
                </p>
                <p className="modalText">
                  Please provide your phone number:
                </p>
                <div className="phone-input-container">
                  <div className="phone-flex-container">
                    <div className="country-code-autocomplete">
                      <input
                        type="text"
                        value={countryCodeInput}
                        onChange={(e) => handleCountryCodeInput(e.target.value)}
                        onFocus={() => setShowCountryDropdown(true)}
                        placeholder="Country code"
                        className="country-code-input"
                      />
                      {showCountryDropdown && filteredCountries.length > 0 && (
                        <div className="country-dropdown">
                          {filteredCountries.slice(0, 5).map((country) => (
                            <div
                              key={country.code}
                              className="country-option"
                              onClick={() => selectCountry(country)}
                            >
                              <span className="country-flag">{country.flag}</span>
                              <span className="country-code">{country.code}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={verificationPhone}
                      onChange={(e) => {
                        // Only allow numbers and basic formatting
                        const value = e.target.value.replace(/[^\d\s\-\(\)]/g, '');
                        setVerificationPhone(value);
                      }}
                      placeholder="Enter your phone number"
                      className="verification-input phone-number-input"
                    />
                  </div>
                </div>
                {error && (
                  <div className="error-message">
                    <strong>Error:</strong> {error}
                    <br />
                    <small>Please ensure your phone number is valid and includes the area code.</small>
                  </div>
                )}
                &nbsp; <button 
                  onClick={handlePhoneSubmit} 
                  className="blueButt" 
                  disabled={verificationLoading}
                >
                  Continue
                </button>
              </div>
            ) : verificationStep === 'email-request' ? (
              <div className="email-request">
                <h2 className="modal-h2">Verify Your Email</h2>
                <p className="modalText2">
                  We'll send a verification code to your email address.
                </p>
                <input
                  type="email"
                  value={verificationEmail && verificationEmail.includes('@') ? verificationEmail : ''}
                  onChange={(e) => setVerificationEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="verification-input"
                />
                {error && <p className="error-message">{error}</p>}
                <button 
                  onClick={handleEmailVerificationRequest} 
                  className="blueButt" 
                  disabled={verificationLoading}
                >
                  Send Verification Code
                </button>
              </div>
            ) : verificationStep === 'email-verify' ? (
              <div className="email-verify">
                <h2 className="modal-h2">Enter Verification Code</h2>
                <p className="modalText">
                  We have just sent you an email with your unique code to: <b>{verificationEmail}</b>  
                  &nbsp; Please enter it below to verify your email:
                </p>
                <input
                  type="text"
                  value={verificationCode && verificationCode !== 'undefined' ? verificationCode : ''}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="verification-input code-input"
                  maxLength="6"
                />
                {error && <p className="error-message">{error}</p>}
                <button 
                  onClick={handleEmailCodeVerify} 
                  className="blueButt" 
                  disabled={verificationLoading}
                >
                  Verify Code
                </button>
                <button 
                  onClick={() => {
                    setError(''); // Clear any error when resending
                    setVerificationCode(''); // Clear the old code
                    setVerificationStep('email-request');
                  }} 
                  className="text-button link-button"
                >
                  Resend Code
                </button>
              </div>
            ) : null}
          </div>
        ) : step === 1 ? (
          <div className="step-1">
            <h2 className="modal-h2">Before we approve you</h2>
            <p className="modalText">
              We'd like for you to tell us why you want to use this technology. Please note that responses will be reviewed as part of the approval process.
              Additionally if you aren't selected, the reservation fee will not be refunded. You will still be able to join us when Intellegacy opens to the public, 
              and receive a special thank you badge for early support, and 3 legacy coins.
            </p>
            <textarea
              value={message && message !== 'undefined' ? message : ''}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="&nbsp;This ought to be good, why do you want to be immortal?"
              maxLength={2300}
              rows={6}
            ></textarea>
            <div className="character-count">{(message || '').length}/2300 characters</div>
            {error && <p className="error-message">{error}</p>}
            <button onClick={handleNextStep} className="blueButt" disabled={isLoading}>
              Continue
            </button>
          </div>
  ) : step === 2 ? (
          <div className="step-2">
            <h2 className="modal-h2 marjory">Intellegacy Plans</h2>
            <div className="calltoActNotButton">
              <p className="ReserveYourSpot">Reserve your spot now and get all the benefits of an <b>Elite Plan</b> user and full access for a year. 
              You'll also get to be amongst the first to use the system, and receive a <b>Legacy badge</b> that'll stay in your account for good.</p>
            </div>
            
            {isMobile ? (
              <MobilePlansSwipe plans={plans} step={step} />
            ) : (
              <div className="plans animated">
                {plans.map((plan, index) => (
                  <div
                    key={plan.name}
                    className={`plan-box ${index === 2 && step === 2 ? "highlighted" : ""}`}
                  >
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="price">{plan.price}</p>
                    <ul className="ululul">
                      {plan.features.map((feature, idx) => (
                        <span key={idx}>
                          <span className="checkmark">✔</span> {feature}
                          <span className="featureSpace"> <br /> </span>
                        </span>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <div className="terms-link-container">
              <input
                type="checkbox"
                id="terms"
                checked={isTermsChecked}
                onChange={() => setIsTermsChecked(!isTermsChecked)}
              /> &nbsp;
              <label htmlFor="terms">
                I've read and I agree to the{" "}
                <span className="terms-link" onClick={() => setShowTerms(true)}>
                  Terms and Conditions
                </span>
                &nbsp;
              </label>
            </div>
            {error && <p className="error-message">{error}</p>}
            <button
              className={`blueButt2 ${isTermsChecked ? "active" : "inactive"}`}
              onClick={handleNextStep}
            >
              Reserve
            </button>
          </div>
        ) : step === 3 ? (
          <div className="step-3">
            <h2 className="modal-h2">Payment</h2>
            
            {userSession.hasPaid ? (
              <div>
                <p className="payment-success-message">Payment completed successfully!</p>
                <p>Thank you for your payment. You can now proceed to the next step.</p>
              </div>
            ) : (
              <div>
                <p>Please select your preferred payment method.</p>
                <div id="paypal-buttons" className="paypal-buttons-container" />
              </div>
            )}
            {error && <p className="error-message">{error}</p>}
            {alreadyPaidClicks < 2 && (
              <button
                className={`blueButt2 ${userSession.hasPaid ? "active" : "inactive"}`}
                onClick={userSession.hasPaid ? handleNextStep : handleAlreadyPaidClick}
                disabled={isLoading}
              >
                {userSession.hasPaid ? "Continue" : 
                  alreadyPaidClicks === 0 ? "I already paid!" : "Yes I have!"
                }
              </button>
            )}
          </div>
          
        ) : (
<div className="step-4">
<p>
    Thank you for joining us on this journey! <img className="smileyFace" src={ smileyFace } alt='smiley face'></img> We will email you as soon as the system
    opens for your use. It may take a while, so we really appreciate your patience and
    support.
  </p><br/>
  <div className="animated-quote">
    {[
      "“Early adopters are those individuals that use new products before the majority of people.",
      "They are risk-takers and trendsetters and have a strong influence on the success of a new product.”",
    ].map((line, index) => (
      <span
        key={index}
        style={{
          animationDelay: `${index * 0.8}s`,
        }}
      >
        {line}&nbsp;
      </span>
    ))}
  </div>
  <p className="quote-author">- Investopedia</p>
  <p><br/>
    We want groups of people who know each other in our initial
    userbase, so we are <b>more likely to approve</b> those who bring friends and
    family. Add people to your friend list to unlock:
  </p>

  <div className="progress-bar">
  {progressMilestones.map((milestone, index) => (
    <div
      key={index}
      className={`milestone ${milestone.achieved ? "achieved" : "not-achieved"}`}
    >
      <img
        src={milestone.achieved ? milestone.icon : milestone.inactiveIcon || milestone.icon}
        alt={milestone.label}
      />
      {milestone.label && <p>{milestone.label}</p>}
    </div>
  ))}
</div>

<p className="linebeforelink">Please choose carefully who you want to invite, because you only have 5 spots to give. For every person who registers with your uniquely generated personal invite link you will get in-app rewards that will increase in value over time.
</p>
  <div className="wrapper">
    <div className="letters">
      {uniqueLink ? uniqueLink.split("").map((letter, index) => (
        <span key={index} className="letter">
          {letter}
        </span>
      )) : (
        <span className="loading-message">Loading referral link...</span>
      )}
    </div>
  </div>
  <div className="button-wrapper">
    <button
      className="blueButt3"
      onClick={() => uniqueLink && navigator.clipboard.writeText(uniqueLink)}
      disabled={!uniqueLink}
    >
      Copy Link
    </button>
    <button 
      className="blueButt4"
      onClick={() => {
        if (navigator.share && uniqueLink) {
          navigator.share({
            title: 'Join Intellegacy',
            text: 'Upload yourself to the cloud, and create an Ai you - the first step to immortality!',
            url: uniqueLink
          }).catch(() => {}); // Silently handle share cancellation
        } else if (uniqueLink) {
          // Fallback for browsers that don't support Web Share API
          navigator.clipboard.writeText(`Join me on Intellegacy! ${uniqueLink}`);
          alert('Share text copied to clipboard');
        }
      }}
      disabled={!uniqueLink}
    >
      <img className="shareButton" src={ share } alt='share'></img>
    </button>
  </div>
<br/>
<p>
This page will remain available to you until you are validated.
</p>


        </div>
      )}
      </div>
    </div>
    </>
  );
};
export default RequestAccessModal;
